import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CheckCircle } from 'lucide-react';
import { PropostaParaContrato } from '@/types/contrato';

interface ContratoConfirmacaoProps {
  propostaSelecionada: PropostaParaContrato;
  onConfirmar: () => void;
  onCancelar: () => void;
}

export default function ContratoConfirmacao({ 
  propostaSelecionada, 
  onConfirmar, 
  onCancelar 
}: ContratoConfirmacaoProps) {
  const formatarTelefone = (telefone: string | undefined) => {
    if (!telefone) return 'Não informado';
    
    // Remove todos os caracteres não numéricos
    const numeroLimpo = telefone.replace(/\D/g, '');
    
    // Verifica se tem 11 dígitos (celular com DDD)
    if (numeroLimpo.length === 11) {
      return `(${numeroLimpo.slice(0, 2)})${numeroLimpo.slice(2, 7)}-${numeroLimpo.slice(7)}`;
    }
    
    // Verifica se tem 10 dígitos (fixo com DDD)
    if (numeroLimpo.length === 10) {
      return `(${numeroLimpo.slice(0, 2)})${numeroLimpo.slice(2, 6)}-${numeroLimpo.slice(6)}`;
    }
    
    // Se não tem o formato esperado, retorna o original
    return telefone;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revisão Geral da Proposta</CardTitle>
        <CardDescription>
          Revise todas as informações da proposta antes de prosseguir com o contrato
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Seção 1: Dados do Cliente */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-blue-800 border-b border-blue-200 pb-2">
            📋 Dados do Cliente
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-gray-600">Nome/Razão Social</Label>
              <p className="text-sm font-semibold">
                {propostaSelecionada.lead_tipo === 'PF' ? propostaSelecionada.lead_nome : propostaSelecionada.lead_razao_social}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Tipo</Label>
              <p className="text-sm">
                {propostaSelecionada.lead_tipo === 'PF' ? 'Pessoa Física' : 'Pessoa Jurídica'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Telefone</Label>
              <p className="text-sm">{formatarTelefone(propostaSelecionada.lead_telefone)}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Grupo Tarifário</Label>
              <p className="text-sm">{propostaSelecionada.lead_grupo_tarifario || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Endereço Completo</Label>
              <p className="text-sm">
                {[
                  propostaSelecionada.lead_endereco,
                  propostaSelecionada.lead_numero,
                  propostaSelecionada.lead_cidade,
                  propostaSelecionada.lead_estado,
                  propostaSelecionada.lead_cep
                ].filter(Boolean).join(', ') || 'Endereço não informado'}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Vendedor</Label>
              <p className="text-sm">{propostaSelecionada.lead_vendedor_nome || 'Não informado'}</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Parceiro</Label>
              <p className="text-sm">{propostaSelecionada.lead_parceiro_nome || 'Não informado'}</p>
            </div>
            {propostaSelecionada.lead_tipo === 'PJ' && propostaSelecionada.lead_representante_legal && (
              <div className="md:col-span-2">
                <Label className="text-sm font-medium text-gray-600">Representante Legal</Label>
                <p className="text-sm">{propostaSelecionada.lead_representante_legal}</p>
              </div>
            )}
          </div>
        </div>

        {/* Seção 2: Dados Gerais do Sistema */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-green-800 border-b border-green-200 pb-2">
            ⚡ Dados Gerais do Sistema
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-green-50 p-4 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-gray-600">Potência do Sistema</Label>
              <p className="text-sm font-semibold">{propostaSelecionada.potencia_total_paineis} kWp</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Número de Placas</Label>
              <p className="text-sm font-semibold">{propostaSelecionada.total_paineis} unidades</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Potência do Inversor</Label>
              <p className="text-sm font-semibold">{propostaSelecionada.potencia_total_inversores} kW</p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Número de Inversores</Label>
              <p className="text-sm font-semibold">
                {(() => {
                  // Calcular número de inversores com base nos insumos dos kits
                  let totalInversores = 0;
                  [propostaSelecionada.kit1_insumos, propostaSelecionada.kit2_insumos, 
                   propostaSelecionada.kit3_insumos, propostaSelecionada.kit4_insumos].forEach(kit => {
                    if (kit?.Insumos) {
                      kit.Insumos.forEach((insumo: any) => {
                        if (insumo.Tipo === 'INVERSOR') {
                          totalInversores += insumo.Quantidade || 0;
                        }
                      });
                    }
                  });
                  return totalInversores || 'Não informado';
                })()}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Tipo de Fixação</Label>
              <p className="text-sm">{propostaSelecionada.lead_tipo_fixacao || 'Não informado'}</p>
            </div>
            {propostaSelecionada.lead_tipo_fixacao === 'Telhado' && (
              <div>
                <Label className="text-sm font-medium text-gray-600">Tipo de Telha</Label>
                <p className="text-sm">{propostaSelecionada.lead_tipo_telha || 'Não informado'}</p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-gray-600">Valor do Kit</Label>
              <p className="text-sm font-semibold">
                R$ {propostaSelecionada.valor_total_kits.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">R$/Wp do Kit</Label>
              <p className="text-sm font-semibold">
                R$ {(propostaSelecionada.kits_r_wp || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        {/* Seção 3: Dados Financeiros do Projeto */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-purple-800 border-b border-purple-200 pb-2">
            💰 Dados Financeiros do Projeto
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50 p-4 rounded-lg">
            <div>
              <Label className="text-sm font-medium text-gray-600">Valor do Projeto</Label>
              <p className="text-sm font-semibold text-purple-700">
                R$ {(propostaSelecionada.valor_total_kits + propostaSelecionada.preco_integracao).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Comissão Vendedor</Label>
              <p className="text-sm">
                R$ {(propostaSelecionada.comissao_vendedor_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Valor do Kit</Label>
              <p className="text-sm">
                R$ {propostaSelecionada.valor_total_kits.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Comissão Parceiro</Label>
              <p className="text-sm">
                R$ {(propostaSelecionada.comissao_parceiro_valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            {propostaSelecionada.comissao_parceiro_pai_valor !== undefined && propostaSelecionada.comissao_parceiro_pai_valor > 0 && (
              <div>
                <Label className="text-sm font-medium text-gray-600">Comissão Parceiro Pai</Label>
                <p className="text-sm">
                  R$ {propostaSelecionada.comissao_parceiro_pai_valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-gray-600">Valor da Integração</Label>
              <p className="text-sm">
                R$ {propostaSelecionada.preco_integracao.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Valor da Mão de Obra</Label>
              <p className="text-sm">
                R$ {(propostaSelecionada.mao_obra || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Valor da Estrutura Metálica</Label>
              <p className="text-sm">
                R$ {(propostaSelecionada.valor_estrutura_metallica || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Custo Total da Integração</Label>
              <p className="text-sm">
                R$ {(propostaSelecionada.custo_total_integracao || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Lucro Líquido Esperado</Label>
              <p className="text-sm font-semibold text-green-600">
                R$ {(propostaSelecionada.lucro_liquido || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-600">Custo Total de Implantação</Label>
              <p className="text-sm font-semibold text-orange-600">
                R$ {(propostaSelecionada.custo_total_implantacao || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={onConfirmar} className="flex-1">
            <CheckCircle className="w-4 h-4 mr-2" />
            Prosseguir com o Contrato
          </Button>
          <Button variant="outline" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
